import { startCase } from 'es-toolkit';
import React from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, List, ListItem, Paper } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { EnclosureClassification, LoadSettings, roofShapes, WindDesignMethod } from '@paragon/contract-library';
import { hasFreeSubscription, UpgradeNotification } from '@paragon/login-tools';
import { humanCase, notNullish } from '@paragon/utilities';

import { showUpgradeAccountDialog } from '../../../../../../store/actions/popupActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { MultiValueAngleInput } from '../../multi-value-controls/MultiValueAngleInput';
import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { MultiValueFeetDistanceInput } from '../../multi-value-controls/MultiValueFeetDistanceInput';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { MultiValueSelectInput } from '../../multi-value-controls/MultiValueSelectInput';
import { asceSection, getAsce7Version, isAsce7_16OrLater } from '../loadingStandards';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { DurationFactorSetting } from './DurationFactorSettings';
import { emphasizedControl } from './emphasizedControl';
import { getTerrainExposureCategoryName } from './getTerrainExposureCategoryName';
import { terrainCategories } from './loadSettingsEnumOptions';

function getWindDesignMethodName(windDesignMethod: WindDesignMethod) {
  switch (windDesignMethod) {
    case 'WindAsceCc':
      return 'ASCE CC';
    case 'WindAsceMwfrsEnvelope':
      return 'ASCE MWFRS Envelope';
    case 'WindAsceMwfrsDirectional':
      return 'ASCE MWFRS Directional';
    case 'WindAsceHybridEnvelope':
      return 'ASCE Hybrid Envelope';
    case 'WindAsceHybridDirectional':
      return 'ASCE Hybrid Directional';
  }
}

function getRigidityFactorASCESection(loadSettingsArray: readonly LoadSettings[]): string {
  const anyRecent = loadSettingsArray.some((setting) => {
    const version = getAsce7Version(setting.buildingCode);

    return version !== null && isAsce7_16OrLater(version);
  });

  return anyRecent ? '26.11' : '26.9';
}

function getGroundElevationASCEReference(loadSettingsArray: readonly LoadSettings[]): string {
  const anyRecent = loadSettingsArray.some((setting) => {
    const version = getAsce7Version(setting.buildingCode);
    return version !== null && isAsce7_16OrLater(version);
  });
  return anyRecent
    ? `Consult ${asceSection('26.9')} for elevation reduction factors`
    : `Consult the commentary to ${asceSection('27.3.2')}`;
}

function getEnclosureClassifications(loadSettingsArray: readonly LoadSettings[]) {
  const includePartiallyOpen = loadSettingsArray.every((loadSettings) => {
    const asce7Version = getAsce7Version(loadSettings.buildingCode);
    return asce7Version != null && isAsce7_16OrLater(asce7Version);
  });

  const includeOpen = loadSettingsArray.every(
    (loadSettings) =>
      loadSettings.windDesignMethod !== 'WindAsceMwfrsEnvelope' &&
      loadSettings.windDesignMethod !== 'WindAsceHybridEnvelope',
  );

  const enclosureClassifications: (EnclosureClassification | null)[] = [
    'Enclosed',
    'PartiallyEnclosed',
    includePartiallyOpen ? 'PartiallyOpen' : null,
    includeOpen ? 'Open' : null,
  ];
  return enclosureClassifications.filter(notNullish);
}

const useSlopedTopChordAngleForFlatTopChordsHelpText =
  "When this is on, the roof surface attached to a truss's flat top chords will be assumed to have the same slope" +
  " as the truss's sloped top chords.";

export function WindLoadSettings({ disabled, loadSettingsArray, setLoadSettingsValues }: LoadSettingsControlProps) {
  const dispatch = useAppDispatch();

  // wind loads are not available to free users
  const isFreeUser = useAppSelector(hasFreeSubscription);
  if (isFreeUser) disabled = true;

  return (
    <Box>
      {isFreeUser && (
        <UpgradeNotification
          sx={{ marginBottom: 1 }}
          briefDescription="wind loads"
          onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
        />
      )}

      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueSelectInput
          sx={{ m: 1, width: '45%' }}
          property="Wind Design Method"
          options={[
            'WindAsceCc',
            'WindAsceMwfrsEnvelope',
            'WindAsceMwfrsDirectional',
            'WindAsceHybridEnvelope',
            'WindAsceHybridDirectional',
          ]}
          values={loadSettingsArray.map((loadSettings) => loadSettings.windDesignMethod)}
          submit={(windDesignMethod) => setLoadSettingsValues({ windDesignMethod })}
          getName={getWindDesignMethodName}
          disabled={disabled}
        />
        <MultiValueNumberInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.windSpeed)}
          submit={(windSpeed) => setLoadSettingsValues({ windSpeed })}
          label="Wind Speed (V)"
          unit="mph"
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueSelectInput
          sx={{ m: 1, width: '45%' }}
          property="Enclosure Classification"
          options={getEnclosureClassifications(loadSettingsArray)}
          values={loadSettingsArray.map((loadSettings) => loadSettings.enclosureClassification)}
          submit={(enclosureClassification) => setLoadSettingsValues({ enclosureClassification })}
          getName={startCase}
          disabled={disabled}
          fullWidth={false}
        />
        <MultiValueSelectInput
          sx={{ m: 1, width: '45%' }}
          property="Terrain Exposure Category"
          options={terrainCategories}
          values={loadSettingsArray.map((loadSettings) => loadSettings.terrainExposureCategory)}
          submit={(terrainExposureCategory) => setLoadSettingsValues({ terrainExposureCategory })}
          getName={getTerrainExposureCategoryName}
          disabled={disabled}
          fullWidth={false}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueSelectInput
          sx={{ m: 1, width: '45%' }}
          property="Roof Shape"
          options={roofShapes}
          values={loadSettingsArray.map((loadSettings) => loadSettings.roofShape)}
          submit={(roofShape) => setLoadSettingsValues({ roofShape })}
          getName={(value) => {
            if (value === 'Gable') return 'Gable or Flat';
            return humanCase(value);
          }}
          disabled={disabled}
          fullWidth={false}
        />
        <MultiValueFeetDistanceInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.meanRoofHeight)}
          submit={(meanRoofHeight) => setLoadSettingsValues({ meanRoofHeight })}
          label="Mean Roof Height"
          disabled={disabled}
        />
      </Box>
      {loadSettingsArray.some((loadSettings) => loadSettings.roofShape === 'Hip') && (
        <Paper sx={emphasizedControl}>
          <List dense>
            <ListItem>
              <MultiValueBooleanInput
                values={loadSettingsArray.map((loadSettings) => loadSettings.useSlopedTopChordAngleForFlatTopChords)}
                onChange={(useSlopedTopChordAngleForFlatTopChords) =>
                  setLoadSettingsValues({ useSlopedTopChordAngleForFlatTopChords })
                }
                label="Roof slope for flat top chords matches that for sloped top chords"
                disabled={disabled}
              />
              <TooltipIconButton title={useSlopedTopChordAngleForFlatTopChordsHelpText}>
                <HelpIcon />
              </TooltipIconButton>
            </ListItem>
            <ListItem>
              <MultiValueAngleInput
                label="Roof Slope for Flat Top Chords"
                defaultDisplayFormat="slope"
                value={loadSettingsArray.map((loadSettings) => loadSettings.angleForFlatTopChords)}
                min={0}
                max={90}
                onSubmit={(angleForFlatTopChords) => setLoadSettingsValues({ angleForFlatTopChords })}
                disabled={
                  disabled ||
                  loadSettingsArray.every((loadSettings) => loadSettings.useSlopedTopChordAngleForFlatTopChords)
                }
              />
            </ListItem>
          </List>
        </Paper>
      )}
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueFeetDistanceInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.horizontalDistanceToWindwardEdge)}
          submit={(horizontalDistanceToWindwardEdge) => setLoadSettingsValues({ horizontalDistanceToWindwardEdge })}
          label="Horizontal distance to windward edge"
          disabled={disabled}
        />
        <MultiValueNumberInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.windDirectionalityFactor)}
          submit={(windDirectionalityFactor) => setLoadSettingsValues({ windDirectionalityFactor })}
          label={
            <span>
              Directionality Factor (K<sub>d</sub>)
              <TooltipIconButton
                // prettier-ignore
                title={
                  <span>
                    Load reduction factor accounting for wind direction. Default value of 0.85 can be applied to
                    buildings, but larger factors are required in special cases, such as chimneys & trussed towers.
                    See {asceSection('26.6')}.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </span>
          }
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueNumberInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.windTopographicFactor)}
          submit={(windTopographicFactor) => setLoadSettingsValues({ windTopographicFactor })}
          label={
            <span>
              Topographic Factor (K<sub>zt</sub>)
              <TooltipIconButton
                // prettier-ignore
                title={
                  <span>
                    Accounts for wind speedup at raised land features, such as hills and escarpments.
                    Consult {asceSection('26.8.2')} for in-depth calculations.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </span>
          }
          disabled={disabled}
        />
        <MultiValueNumberInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.groundElevationFactor)}
          submit={(groundElevationFactor) => setLoadSettingsValues({ groundElevationFactor })}
          label={
            <span>
              Ground Elevation Factor (K<sub>e</sub>)
              <TooltipIconButton
                title={
                  <span>
                    Accounts for reduced air density due to ground elevation. A value of 1.0 can be used for all
                    elevations. {getGroundElevationASCEReference(loadSettingsArray)}.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </span>
          }
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueNumberInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.windGustEffectFactor)}
          submit={(windGustEffectFactor) => setLoadSettingsValues({ windGustEffectFactor })}
          label={
            <span>
              Gust Effect Factor (G)
              <TooltipIconButton
                // prettier-ignore
                title={
                  <span>
                    Accounts for rigidity of the structure. Factor of 0.85 is permitted for rigid buildings.
                    Consult {getRigidityFactorASCESection(loadSettingsArray)} for approaches to determining when a building is rigid.
                  </span>
                }
              >
                <HelpIcon />
              </TooltipIconButton>
            </span>
          }
          disabled={disabled}
        />
        <DurationFactorSetting
          sx={{ m: 1, width: '45%' }}
          loadType="wind"
          loadSettingsArray={loadSettingsArray}
          setLoadSettingsValues={setLoadSettingsValues}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isLeftEndVerticalExposed)}
          onChange={(isLeftEndVerticalExposed) => setLoadSettingsValues({ isLeftEndVerticalExposed })}
          label="Left End Vertical exposed"
          disabled={disabled}
        />
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isRightEndVerticalExposed)}
          onChange={(isRightEndVerticalExposed) => setLoadSettingsValues({ isRightEndVerticalExposed })}
          label="Right End Vertical exposed"
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isLeftPorchExposed)}
          onChange={(isLeftPorchExposed) => setLoadSettingsValues({ isLeftPorchExposed })}
          label="Left Porch exposed"
          disabled={disabled}
        />
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isRightPorchExposed)}
          onChange={(isRightPorchExposed) => setLoadSettingsValues({ isRightPorchExposed })}
          label="Right Porch exposed"
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isLeftCantileverExposed)}
          onChange={(isLeftCantileverExposed) => setLoadSettingsValues({ isLeftCantileverExposed })}
          label="Left Cantilever exposed"
          disabled={disabled}
        />
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.isRightCantileverExposed)}
          onChange={(isRightCantileverExposed) => setLoadSettingsValues({ isRightCantileverExposed })}
          label="Right Cantilever exposed"
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-evenly', p: 1 }}>
        <MultiValueFeetDistanceInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.buildingWidth)}
          submit={(buildingWidth) => setLoadSettingsValues({ buildingWidth })}
          label="Building Width"
          disabled={disabled}
        />
        <MultiValueFeetDistanceInput
          sx={{ m: 1, width: '45%' }}
          values={loadSettingsArray.map((loadSettings) => loadSettings.buildingLength)}
          submit={(buildingLength) => setLoadSettingsValues({ buildingLength })}
          label="Building Length"
          disabled={disabled}
        />
      </Box>
    </Box>
  );
}
